/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';

import {
	useUpdateUnitMutation,
	useUpdateFlexUnitMutation,
	unwrapError,
	useAddRecurringShiftUnitMutation
} from '../../../redux/units';
import { showNotification } from '../../../redux/notification';

import { UNIT_ASSIGNMENT_MODALS } from '../../../constants/UnitAssignment';
import EditUnitModal from './EditUnitModal';
import UnassignUnitModal from './UnassignUnitModal';
import EditAlreadyArrivedModal from './EditAlreadyArrivedModal';
import UnassignAlreadyArrivedModal from './UnassignAlreadyArrivedModal';

import './UnitAssignment.scss';

const UnitAssignment = ({
	availableUnits,
	setIsLoading,
	setShowModal,
	showModal,
	slot,
	showOnDemandShiftTypeBadges,
	onFinish,
	isRecurring = false,
	preventSuccessToast
}) => {
	const {
		resource_rate_id: resourceRateId,
		slot_id: slotId,
		unit_id: unitId,
		unit_name: unitName,
		facility_id: facilityId,
		is_flex: isFlex,
		contract_id: contractId,
		caregiver_id: caregiverId
	} = slot;
	const [selectedUnit, setSelectedUnit] = useState(availableUnits.find((_unit) => _unit?.id === unitId));
	const [updateUnit, updateUnitState] = useUpdateUnitMutation();
	const [updateFlexUnit, updateFlexUnitState] = useUpdateFlexUnitMutation();
	const [addRecurringShiftUnit, addRecurringShiftUnitState] = useAddRecurringShiftUnitMutation();

	const dispatch = useDispatch();

	const onEditAssignment = () => {
		if (setIsLoading) {
			setIsLoading(true);
		}
		setShowModal(false);

		if (isRecurring) {
			addRecurringShiftUnit({
				unitId: selectedUnit?.id,
				shiftDate: moment.utc(slot.raw_start_time).format('YYYY-MM-DD'),
				shiftId: slot.shift_id,
				oldUnitId: unitId,
				oldCaregiverId: caregiverId
			});
		} else {
			updateUnit({
				...(!contractId ? { resourceRateId } : null),
				slotId,
				unitId: selectedUnit?.id,
				oldUnitId: unitId,
				oldCaregiverId: caregiverId
			});
		}
	};

	const onEditFlexAssignment = () => {
		if (setIsLoading) {
			setIsLoading(true);
		}
		setShowModal(false);
		updateFlexUnit({
			facilityId,
			slotId,
			unitId: selectedUnit?.id || null,
			oldUnitId: unitId,
			oldCaregiverId: caregiverId
		});
	};

	const handleEditUnit = () => {
		if (isFlex) {
			onEditFlexAssignment();
		} else {
			onEditAssignment();
		}
	};

	const onCancelEdit = () => {
		setSelectedUnit(availableUnits.filter((_unit) => _unit?.id === unitId)[0]);
		setShowModal(false);
	};

	const reloadFeed = useCallback(
		async (isSuccess) => {
			await onFinish(isSuccess);
			if (setIsLoading) {
				setIsLoading(false);
			}
		},
		[onFinish, setIsLoading]
	);

	useEffect(() => {
		let operationState;

		if (isFlex) {
			operationState = updateFlexUnitState;
		} else if (isRecurring) {
			operationState = addRecurringShiftUnitState;
		} else {
			operationState = updateUnitState;
		}

		const { isLoading, isError, isSuccess, error } = operationState;

		if (isSuccess && !preventSuccessToast) {
			const message = selectedUnit?.unit_name
				? `Unit was successfully updated to ${selectedUnit?.unit_name}.`
				: `${unitName} has been successfully removed from this shift.`;

			dispatch(showNotification('success', message));
		}
		if (isError && error) {
			dispatch(showNotification('error', unwrapError(error) || 'Failed to update unit. Please try again!'));
		}
		if (!isLoading && (isSuccess || isError)) {
			reloadFeed(isSuccess).catch((e) =>
				dispatch(showNotification('error', unwrapError(e) || 'Failed to update unit. Please try again!'))
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateFlexUnitState, updateUnitState, addRecurringShiftUnitState]);

	return (
		<div className="unit-assignment">
			{showModal === UNIT_ASSIGNMENT_MODALS.edit && (
				<EditUnitModal
					slot={slot}
					onCancel={onCancelEdit}
					onEditAssignment={handleEditUnit}
					setShowModal={setShowModal}
					selectedUnit={selectedUnit}
					setSelectedUnit={setSelectedUnit}
					availableUnits={availableUnits}
					showOnDemandShiftTypeBadges={showOnDemandShiftTypeBadges}
				/>
			)}
			{showModal === UNIT_ASSIGNMENT_MODALS.unassign && (
				<UnassignUnitModal
					onCancel={() => setShowModal(null)}
					onEditAssignment={handleEditUnit}
					setSelectedUnit={setSelectedUnit}
					selectedUnit={selectedUnit}
					slot={slot}
				/>
			)}
			{showModal === UNIT_ASSIGNMENT_MODALS.editArrived && (
				<EditAlreadyArrivedModal onCancel={() => setShowModal(null)} setShowModal={setShowModal} slot={slot} />
			)}
			{showModal === UNIT_ASSIGNMENT_MODALS.unassignArrived && (
				<UnassignAlreadyArrivedModal
					onCancel={() => setShowModal(null)}
					onEditAssignment={onEditAssignment}
					setSelectedUnit={setSelectedUnit}
					selectedUnit={selectedUnit}
					slot={slot}
				/>
			)}
		</div>
	);
};

export default UnitAssignment;
